import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const navRef = useRef(null);
  const navigate = useNavigate();

  // Language content
  const translations = {
    en: {
      nav: {
        about: 'About',
        features: 'Features',
        whyUs: 'Why Us',
        testimonials: 'Testimonials',
        solutions: 'Solutions',
        resources: 'Resources',
        getStarted: 'Get Started',
        contactUs: 'Contact Us'
      },
      solutions: {
        commercial: 'Commercial Solutions',
        utility: 'Utility Scale',
        residential: 'Residential',
        agricultural: 'Agricultural',
        commercialDesc: 'Business and industrial solar tracking',
        utilityDesc: 'Large-scale solar farm installations',
        residentialDesc: 'Home solar tracking systems',
        agriculturalDesc: 'Farm and irrigation solar solutions'
      },
      resources: {
        guides: 'Installation Guides',
        caseStudies: 'Case Studies',
        technical: 'Technical Docs',
        calculator: 'ROI Calculator',
        guidesDesc: 'Step-by-step installation manuals',
        caseStudiesDesc: 'Real-world success stories',
        technicalDesc: 'Technical specifications & manuals',
        calculatorDesc: 'Calculate your solar savings'
      }
    },
    ar: {
      nav: {
        about: 'من نحن',
        features: 'المميزات',
        whyUs: 'لماذا نحن',
        testimonials: 'آراء العملاء',
        solutions: 'الحلول',
        resources: 'المصادر',
        getStarted: 'ابدأ الآن',
        contactUs: 'اتصل بنا'
      },
      solutions: {
        commercial: 'حلول تجارية',
        utility: 'المرافق الخدمية',
        residential: 'سكني',
        agricultural: 'زراعي',
        commercialDesc: 'أنظمة التتبع الشمسي للأعمال والصناعة',
        utilityDesc: 'تركيبات مزارع الطاقة الشمسية الكبيرة',
        residentialDesc: 'أنظمة التتبع الشمسي للمنازل',
        agriculturalDesc: 'حلول الطاقة الشمسية للمزارع والري'
      },
      resources: {
        guides: 'أدلة التركيب',
        caseStudies: 'دراسات الحالة',
        technical: 'الوثائق الفنية',
        calculator: 'حاسبة العائد',
        guidesDesc: 'أدلة تركيب خطوة بخطوة',
        caseStudiesDesc: 'قصص نجاح حقيقية',
        technicalDesc: 'المواصفات الفنية والأدلة',
        calculatorDesc: 'احسب مدخراتك من الطاقة الشمسية'
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
      
      // Update active section based on scroll position
      const sections = ['home', 'about', 'features', 'problem', 'testimonials', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;
          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
        setIsSolutionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setIsLanguageOpen(false);
    setIsSolutionsOpen(false);
  };

  const changeLanguage = (langCode) => {
    setCurrentLanguage(langCode);
    setIsLanguageOpen(false);
    
    // In a real app, you would update the entire app's language context here
    console.log(`Language changed to: ${langCode}`);
    
    // For RTL languages, you might want to update the document direction
    if (langCode === 'ar') {
      document.documentElement.dir = 'rtl';
      document.documentElement.lang = 'ar';
    } else {
      document.documentElement.dir = 'ltr';
      document.documentElement.lang = 'en';
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
      setIsMenuOpen(false);
    }
  };

  const handleGetStarted = () => {
    // Scroll to contact section
    scrollToSection('contact');
    
    // You could also trigger a modal, form, or other action here
    console.log('Get Started button clicked');
    
    // Example: Track analytics
    // analytics.track('get_started_clicked');
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    
    // If we're already on the home page, scroll to top
    if (window.location.pathname === '/' || window.location.pathname === '/home') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveSection('home');
    } else {
      // Navigate to home page
      navigate('/');
      // Reset scroll position after navigation
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setActiveSection('home');
      }, 100);
    }
    
    setIsMenuOpen(false);
  };

  const handlePageNavigation = (path, e) => {
    e.preventDefault();
    
    // Close mobile menu
    setIsMenuOpen(false);
    
    // If navigating to a different page
    if (window.location.pathname !== path) {
      navigate(path);
    }
    
    // Scroll to top of the new page
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const navItems = [
    { name: translations[currentLanguage].nav.about, href: '#about', id: 'about', type: 'section' },
    { name: translations[currentLanguage].nav.features, href: '#features', id: 'features', type: 'section' },
    { name: translations[currentLanguage].nav.whyUs, href: '#problem', id: 'problem', type: 'section' },
    { name: translations[currentLanguage].nav.testimonials, href: '#testimonials', id: 'testimonials', type: 'section' },
  ];

  const solutionsItems = [
    { 
      name: translations[currentLanguage].solutions.commercial, 
      href: '/commercial',
      description: translations[currentLanguage].solutions.commercialDesc,
      icon: '🏢',
      badge: 'Popular'
    },
    { 
      name: translations[currentLanguage].solutions.utility, 
      href: '/utility-scale',
      description: translations[currentLanguage].solutions.utilityDesc,
      icon: '⚡',
      badge: 'Large Scale'
    },
    { 
      name: translations[currentLanguage].solutions.residential, 
      href: '/residential',
      description: translations[currentLanguage].solutions.residentialDesc,
      icon: '🏠',
      badge: 'New'
    },
    { 
      name: translations[currentLanguage].solutions.agricultural, 
      href: '/agricultural',
      description: translations[currentLanguage].solutions.agriculturalDesc,
      icon: '🌾',
      badge: 'Efficient'
    }
  ];

  const resourcesItems = [
    { 
      name: translations[currentLanguage].resources.guides, 
      href: '/guides',
      description: translations[currentLanguage].resources.guidesDesc,
      icon: '📖'
    },
    { 
      name: translations[currentLanguage].resources.caseStudies, 
      href: '/case-studies',
      description: translations[currentLanguage].resources.caseStudiesDesc,
      icon: '📊'
    },
    { 
      name: translations[currentLanguage].resources.technical, 
      href: '/technical',
      description: translations[currentLanguage].resources.technicalDesc,
      icon: '🔧'
    },
    { 
      name: translations[currentLanguage].resources.calculator, 
      href: '/calculator',
      description: translations[currentLanguage].resources.calculatorDesc,
      icon: '💰'
    }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'ar', name: 'العربية', flag: '🇪🇬', rtl: true }
  ];

  return (
    <nav 
      ref={navRef}
      className={`fixed w-full z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-2xl py-2 border-b border-white/20' 
          : 'bg-white/80 backdrop-blur-lg shadow-sm py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo Section with Your Original Logo */}
          <div className="flex items-center space-x-8">
            <a 
              href="/" 
              className="flex items-center group"
              onClick={handleLogoClick}
            >
              <div className="relative">
                {/* Your Original Logo */}
                <img
                  src="/logo1.png"
                  alt="DuaAxis Logo"
                  className="h-12 w-auto transition-all duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-primary-500 opacity-0 group-hover:opacity-10 rounded-full transition-opacity duration-300"></div>
              </div>
              <div className="ml-3 flex flex-col">
                <span className="text-primary-600 font-bold text-2xl tracking-tight leading-none">
                  DuaAxis
                </span>
                <span className="text-secondary-500 text-xs font-semibold tracking-wide">
                  SOLAR TRACKING
                </span>
              </div>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`relative group px-4 py-2 rounded-xl transition-all duration-300 font-medium text-sm ${
                    activeSection === item.id
                      ? 'text-primary-600 bg-primary-50 shadow-inner'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50/50'
                  }`}
                >
                  {item.name}
                  {activeSection === item.id && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-500 rounded-full"></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 to-secondary-500/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              ))}

              {/* Solutions Dropdown */}
              <div className="relative group">
                <button
                  onClick={() => setIsSolutionsOpen(!isSolutionsOpen)}
                  className="flex items-center px-4 py-2 rounded-xl text-gray-600 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-300 font-medium text-sm"
                >
                  {translations[currentLanguage].nav.solutions}
                  <svg 
                    className={`w-4 h-4 ml-1 transition-transform duration-300 ${isSolutionsOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>

                {/* Solutions Dropdown Menu */}
                <div className={`absolute top-full left-0 mt-2 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden transition-all duration-300 ${
                  isSolutionsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                }`}>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                      {translations[currentLanguage].nav.solutions}
                    </h3>
                    <div className="space-y-2">
                      {solutionsItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="flex items-center p-3 rounded-xl hover:bg-primary-50/50 transition-all duration-300 group"
                          onClick={(e) => handlePageNavigation(item.href, e)}
                        >
                          <span className="text-2xl mr-3 group-hover:scale-110 transition-transform duration-300">{item.icon}</span>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900 group-hover:text-primary-600">{item.name}</span>
                              {item.badge && (
                                <span className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs px-2 py-0.5 rounded-full">
                                  {item.badge}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          </div>
                          <svg className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transform group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                          </svg>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Resources Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center px-4 py-2 rounded-xl text-gray-600 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-300 font-medium text-sm"
                >
                  {translations[currentLanguage].nav.resources}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </button>

                {/* Resources Dropdown Menu */}
                <div className="absolute top-full left-0 mt-2 w-72 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 -translate-y-2 transition-all duration-300">
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">
                      {translations[currentLanguage].nav.resources}
                    </h3>
                    <div className="space-y-2">
                      {resourcesItems.map((item) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          className="flex items-center p-3 rounded-xl hover:bg-primary-50/50 transition-all duration-300 group"
                          onClick={(e) => handlePageNavigation(item.href, e)}
                        >
                          <span className="text-xl mr-3 group-hover:scale-110 transition-transform duration-300">{item.icon}</span>
                          <div className="flex-1">
                            <span className="font-medium text-gray-900 group-hover:text-primary-600">{item.name}</span>
                            <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Language Selector */}
            <div className="hidden md:flex relative">
              <button 
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-xl text-gray-600 hover:text-primary-600 hover:bg-primary-50/50 transition-all duration-300 font-medium text-sm group"
              >
                <span className="text-lg">{languages.find(lang => lang.code === currentLanguage)?.flag}</span>
                <span>{currentLanguage.toUpperCase()}</span>
                <svg 
                  className={`w-4 h-4 transition-transform duration-300 ${isLanguageOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              {/* Language Dropdown */}
              <div className={`absolute top-full right-0 mt-2 w-40 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 overflow-hidden transition-all duration-300 ${
                isLanguageOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
              }`}>
                {languages.map((language) => (
                  <button
                    key={language.code}
                    className={`flex items-center space-x-3 w-full px-4 py-3 text-left hover:bg-primary-50/50 transition-colors duration-200 first:rounded-t-2xl last:rounded-b-2xl ${
                      currentLanguage === language.code ? 'bg-primary-50 text-primary-600' : ''
                    }`}
                    onClick={() => changeLanguage(language.code)}
                  >
                    <span className="text-xl">{language.flag}</span>
                    <span className="font-medium">{language.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Get Started Button - Now Functional */}
            <div className="hidden md:flex">
              <button
                onClick={handleGetStarted}
                className="group relative inline-flex items-center px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-secondary-600 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative z-10 flex items-center">
                  {translations[currentLanguage].nav.getStarted}
                  <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
                  </svg>
                </span>
                <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                type="button"
                onClick={toggleMenu}
                className="relative inline-flex items-center justify-center p-3 rounded-xl text-gray-600 hover:text-primary-600 hover:bg-primary-50/50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-300 group"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                <div className="relative w-6 h-6">
                  <span className={`absolute left-0 top-1 w-6 h-0.5 bg-current transform transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
                  <span className={`absolute left-0 top-3 w-6 h-0.5 bg-current transform transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
                  <span className={`absolute left-0 top-5 w-6 h-0.5 bg-current transform transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
                </div>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-500/0 to-secondary-500/0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-white/95 backdrop-blur-xl shadow-2xl border-t border-white/20 transform transition-all duration-500 ease-in-out overflow-hidden ${
          isMenuOpen ? 'max-h-screen opacity-100 translate-y-0' : 'max-h-0 opacity-0 -translate-y-4'
        }`}
      >
        <div className="px-4 pt-4 pb-6 space-y-2">
          {/* Navigation Items */}
          {navItems.map((item) => (
            <button 
              key={item.id} 
              onClick={() => scrollToSection(item.id)}
              className={`w-full text-left py-3 px-4 rounded-xl transition-all duration-300 font-medium ${
                activeSection === item.id
                  ? 'text-primary-600 bg-primary-50 shadow-inner'
                  : 'text-gray-700 hover:text-primary-600 hover:bg-primary-50'
              }`}
            >
              {item.name}
            </button>
          ))}

          {/* Solutions Section */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="px-4 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {translations[currentLanguage].nav.solutions}
            </h3>
            <div className="space-y-2">
              {solutionsItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="flex items-center py-3 px-4 rounded-xl text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-all duration-300"
                  onClick={(e) => {
                    handlePageNavigation(item.href, e);
                    setIsMenuOpen(false);
                  }}
                >
                  <span className="text-xl mr-3">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                  {item.badge && (
                    <span className="ml-auto bg-primary-100 text-primary-700 text-xs px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Resources Section */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="px-4 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
              {translations[currentLanguage].nav.resources}
            </h3>
            <div className="space-y-2">
              {resourcesItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="flex items-center py-3 px-4 rounded-xl text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-all duration-300"
                  onClick={(e) => {
                    handlePageNavigation(item.href, e);
                    setIsMenuOpen(false);
                  }}
                >
                  <span className="text-xl mr-3">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
            </div>
          </div>

          {/* Language Selector Mobile */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="px-4 text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Language</h3>
            <div className="grid grid-cols-2 gap-2 px-4">
              {languages.map((language) => (
                <button
                  key={language.code}
                  className={`flex items-center justify-center space-x-2 py-2 px-3 rounded-xl border transition-all duration-300 ${
                    currentLanguage === language.code
                      ? 'bg-primary-50 border-primary-200 text-primary-600'
                      : 'border-gray-200 text-gray-700 hover:bg-primary-50/50'
                  }`}
                  onClick={() => changeLanguage(language.code)}
                >
                  <span className="text-lg">{language.flag}</span>
                  <span className="font-medium">{language.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile CTA */}
          <div className="border-t border-gray-100 pt-4">
            <button
              onClick={handleGetStarted}
              className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
            >
              {translations[currentLanguage].nav.getStarted}
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;